import React from "react";
import { Loader2, Lock, Clock } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: no "@" alias under Jest (cf. components/ui/LoadFailure.jsx).
import LoadFailure from "../../components/ui/LoadFailure";
import { adminHref } from "../adminPath";
import { useEntitlements } from "./EntitlementsContext";

/** A panel that says why a page is not open, in place of the page. */
function CapabilityPanel({ state, eta, testId }) {
  const t = useT("admin");
  const comingSoon = state === "coming_soon";
  const Icon = comingSoon ? Clock : Lock;
  return (
    <div data-testid={testId} className="max-w-2xl mx-auto text-center py-24 px-6">
      <Icon className="w-8 h-8 mx-auto text-eh-muted mb-4" />
      <h2 className="font-display text-3xl text-eh-ink mb-3">
        {comingSoon ? t("ent.comingSoon.title") : t("ent.notInPlan.title")}
      </h2>
      <p className="text-eh-muted">{comingSoon ? t("ent.comingSoon.body") : t("ent.notInPlan.body")}</p>
      {comingSoon && eta && (
        <p className="text-eh-muted text-sm mt-2" data-testid={`${testId}-eta`}>{t("ent.comingSoon.eta", { eta })}</p>
      )}
      {!comingSoon && (
        // A plain anchor, like AdminLayout's billing link: it must resolve on the platform
        // host and on a tenant host, where the console is mounted by a second route tree.
        <a
          href={adminHref("/admin/billing")}
          data-testid={`${testId}-billing`}
          className="inline-flex mt-6 btn-primary text-sm"
        >
          {t("ent.notInPlan.cta")}
        </a>
      )}
    </div>
  );
}

/**
 * Route guard: renders `children` only when `capability` is granted AND built.
 *
 * The page is not mounted at all otherwise — mounting it would fire reads the server
 * refuses (403 CAPABILITY_NOT_IN_PLAN / 503 CAPABILITY_NOT_BUILT) and leave the page to
 * invent its own explanation. While the map is loading nothing is claimed either way;
 * when it could not be read, the failure is said and can be retried.
 */
export default function RequireCapability({ capability, children }) {
  const tc = useT("common");
  const ent = useEntitlements();

  if (ent.phase === "loading") {
    return (
      <div className="flex justify-center py-16" data-testid="capability-guard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-eh-muted" />
      </div>
    );
  }
  if (ent.phase === "error") {
    return (
      <LoadFailure
        message={tc("errors.loadFailed")}
        onRetry={ent.reload}
        retryLabel={tc("actions.retry")}
        testId="capability-guard-load-error"
        retryTestId="capability-guard-retry"
      />
    );
  }
  const state = ent.status(capability);
  if (state === "granted") return children;
  return <CapabilityPanel state={state} eta={ent.eta(capability)} testId="capability-guard" />;
}
